import type { Task } from "@/contexts/TaskContext";

/**
 * Single source of truth for mapping database task rows to the Task interface.
 *
 * Options:
 * - `withImages` (default true): include image URLs in the mapped object.
 *   Pass false for metadata-only loads (initial list without images).
 */
export function mapDbTaskToTask(
  dbTask: any,
  options?: { withImages?: boolean }
): Task {
  const withImages = options?.withImages ?? true;

  const originalImageUrls: string[] | undefined = withImages
    ? (dbTask.image_urls || (dbTask.image_url ? [dbTask.image_url] : []))
    : undefined;

  // Prefer server-normalized (white-background) PNGs where available.
  // A non-empty entry in image_normalized_urls means "we rebuilt this image
  // with a white paper background". The frontend must not invert colors.
  const normalizedList: (string | null)[] = Array.isArray(dbTask.image_normalized_urls)
    ? dbTask.image_normalized_urls
    : [];

  const imageUrls = originalImageUrls
    ? originalImageUrls.map((orig, i) => (normalizedList[i] || orig))
    : undefined;

  const imageIsNormalizedDiagram = originalImageUrls
    ? originalImageUrls.map((_, i) => Boolean(normalizedList[i]))
    : undefined;

  // Skip the thumbnail (which is still on a black background) for slots that
  // were normalized — the srcSet must not fall back to it.
  // For premium tasks we always skip thumbs: originals are transparent PNGs
  // that render fine on the CSS white paper, while thumb JPGs baked in a
  // black background at upload time.
  const rawThumbs: string[] | undefined = withImages ? (dbTask.image_thumbnails || undefined) : undefined;
  const isPremium = Boolean(dbTask.is_premium);
  const imageThumbnails = rawThumbs
    ? (rawThumbs.map((t, i) =>
        isPremium || imageIsNormalizedDiagram?.[i] ? undefined : t
      ) as any)
    : rawThumbs;

  const imageCompressed = withImages
    ? (dbTask.image_compressed || undefined)
    : undefined;

  // maxPoints: aggregate from multi_table_data when there are multiple tables,
  // otherwise use the DB column or derive from is_two_points flag.
  const multiTableData = dbTask.multi_table_data;
  const hasMultiTable =
    multiTableData && Array.isArray(multiTableData) && multiTableData.length > 1;

  const maxPoints = hasMultiTable
    ? (multiTableData as any[]).reduce(
        (sum: number, entry: any) => sum + (entry.maxPoints || 1),
        0
      )
    : (dbTask.max_points || (dbTask.is_two_points ? 2 : 1));

  // customScoringPoints lives inside the JSONB custom_scoring_scenarios column
  const customScoringPoints =
    (dbTask.custom_scoring_scenarios as any)?.customScoringPoints || undefined;

  // image dimensions — only include if actually populated
  const imageDimensions =
    dbTask.image_dimensions &&
    Array.isArray(dbTask.image_dimensions) &&
    dbTask.image_dimensions.length > 0
      ? dbTask.image_dimensions
      : undefined;

  const imageBlurData =
    dbTask.image_blur_data &&
    Array.isArray(dbTask.image_blur_data) &&
    dbTask.image_blur_data.some((b: string) => b)
      ? dbTask.image_blur_data.map((b: string, i: number) =>
          imageIsNormalizedDiagram?.[i] ? undefined : b
        )
      : undefined;

  const instructionIsNormalizedDiagram = Boolean(dbTask.instruction_image_normalized_url);
  const instructionImageUrl = dbTask.instruction_image_normalized_url
    || dbTask.instruction_image_url
    || undefined;
  const instructionImageThumbnail = instructionIsNormalizedDiagram || Boolean(dbTask.is_premium)
    ? undefined
    : (dbTask.instruction_image_thumbnail || undefined);
  const instructionImageBlur = instructionIsNormalizedDiagram
    ? undefined
    : (dbTask.instruction_image_blur || undefined);

  return {
    id: dbTask.id,
    slug: dbTask.slug || undefined,
    question: dbTask.question,
    instruction: dbTask.instruction || undefined,
    sources: dbTask.sources || undefined,
    answers: dbTask.answers,
    correctIndex: dbTask.correct_index,
    categories: dbTask.categories || (dbTask.category ? [dbTask.category] : []),
    year: dbTask.year || undefined,
    documentType: dbTask.document_type || undefined,
    formula: dbTask.formula || undefined,
    taskNumber: dbTask.task_number || undefined,
    subpoint: dbTask.subpoint || undefined,
    imageUrls,
    imageThumbnails,
    imageCompressed,
    taskType: dbTask.task_type || undefined,
    taskSubtype: dbTask.task_subtype || undefined,
    useYesNo: dbTask.use_yes_no || false,
    modelAnswer: dbTask.model_answer || undefined,
    prefilledAnswer: dbTask.prefilled_answer || undefined,
    operationalVerb: dbTask.operational_verb || undefined,
    tableData: dbTask.table_data || undefined,
    matchingData: dbTask.matching_data || undefined,
    strikethroughText: dbTask.strikethrough_text || undefined,
    isTwoPoints: dbTask.is_two_points || false,
    maxPoints,
    isMultiSelect: dbTask.is_multi_select || false,
    tableScoringMode: dbTask.table_scoring_mode || null,
    customScoringScenarios: dbTask.custom_scoring_scenarios || undefined,
    customScoringPoints,
    verdictOptions: dbTask.verdict_options || undefined,
    multiTableData: multiTableData || undefined,
    isPremium: dbTask.is_premium || false,
    instructionImageUrl,
    instructionImageThumbnail,
    imageDimensions,
    instructionImageWidth: dbTask.instruction_image_width || undefined,
    instructionImageHeight: dbTask.instruction_image_height || undefined,
    imageBlurData,
    instructionImageBlur,
    imageIsNormalizedDiagram,
    instructionImageIsNormalizedDiagram: instructionIsNormalizedDiagram || undefined,
    // Bilingual PL TRANSLATION fields (main fields above hold EN original).
    // Present only for bilingual document types; null/empty when translation pending.
    questionPl: dbTask.question_pl || undefined,
    instructionPl: dbTask.instruction_pl || undefined,
    answersPl: dbTask.answers_pl || undefined,
    tableDataPl: dbTask.table_data_pl || undefined,
    multiTableDataPl: dbTask.multi_table_data_pl || undefined,
    matchingDataPl: dbTask.matching_data_pl || undefined,
    strikethroughTextPl: dbTask.strikethrough_text_pl || undefined,
    prefilledAnswerPl: dbTask.prefilled_answer_pl || undefined,
    sourcesPl: dbTask.sources_pl || undefined,
    verdictOptionsPl: dbTask.verdict_options_pl || undefined,
  };
}

